import React, { useState } from "react";
import { Link } from "react-router-dom";
import "animate.css";
import "./PartnerWithUs.css";

import Navbar from "../../Navbar/Navbar";
import MainBodyContainer from "../../Common/Body/MainBodyContainer/MainBodyContainer";
import Footer from "../../Footer/Footer";
import { GetInvolvedData } from "../../Data/GetInvolvedData";

// Placeholder images
import placeholderHero from "../../../images/programs/women-4-women-hero.png";

// Partner logos
import mayorLogo from "../../../images/logos/logo-mayor-of-london.png";
import sseLogo from "../../../images/logos/logo-sse.png";
import shpresaLogo from "../../../images/logos/logo-shpresa.png";
import highfieldsLogo from "../../../images/logos/logo-highfields.png";
import glaLogo from "../../../images/logos/logo-gla.png";
import futureYouthLogo from "../../../images/logos/logo-future-youth-zone.jpg";
import disabilityLogo from "../../../images/logos/logo-disability-confident.png";
import barkingLogo from "../../../images/logos/logo-barking-dagenham.svg";

const PartnerWithUs = () => {
  const [activeTab, setActiveTab] = useState("employers");
  
  const partnerData = GetInvolvedData.partner;

  const handleScroll = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div>
      <Navbar />
      
      <div className="partner-with-us-container">
        {/* 1. Hero Section */}
        <section className="partner-hero">
          <img 
            src={placeholderHero} 
            alt="Community collaboration" 
            className="partner-hero-bg" 
            onError={(e) => { e.target.src = 'https://via.placeholder.com/1920x1080?text=Partner+Hero+Image'; }}
          />
          <div className="partner-hero-content animate__animated animate__fadeInUp">
            <h1>Collaborate to Amplify Community Impact.</h1>
            <p>
              We can't meet all community needs alone. Join us in breaking down barriers, 
              empowering individuals, and building a stronger, more inclusive East London.
            </p>
            <div className="partner-hero-ctas">
              <button className="btn-primary" onClick={() => { setActiveTab('employers'); handleScroll('pathways') }}>
                Hire Diverse Talent
              </button>
              <button className="btn-secondary" onClick={() => { setActiveTab('funders'); handleScroll('pathways') }}>
                Fund Our Mission
              </button>
              <button className="btn-secondary" onClick={() => { setActiveTab('organizations'); handleScroll('pathways') }}>
                Explore Partnerships
              </button>
            </div>
          </div>
        </section>

        {/* 2. Value Proposition Section */}
        <section className="value-proposition-section">
          <h2 className="section-title">Why Partner with APT?</h2>
          <p className="section-subtitle">Because community-led change works.</p>
          
          <div className="value-grid">
            <div className="value-card animate__animated animate__zoomIn">
              <div className="value-icon">👥</div>
              <h3>Lived Experience</h3>
              <p>Our programs are designed and delivered by people who understand the community's realities firsthand.</p>
            </div>
            
            <div className="value-card animate__animated animate__zoomIn" style={{ animationDelay: '0.1s' }}>
              <div className="value-icon">🧩</div>
              <h3>Holistic Support</h3>
              <p>We don't just treat symptoms. We address root causes—from ESOL and skills training to housing advocacy.</p>
            </div>
            
            <div className="value-card animate__animated animate__zoomIn" style={{ animationDelay: '0.2s' }}>
              <div className="value-icon">📈</div>
              <h3>Proven Impact</h3>
              <p>78‑85% course completion, £45,000+ in secured benefits, and 170+ advocacy cases in 2024-25.</p>
            </div>
            
            <div className="value-card animate__animated animate__zoomIn" style={{ animationDelay: '0.3s' }}>
              <div className="value-icon">⚡</div>
              <h3>Agile & Efficient</h3>
              <p>As a Community Interest Company, every pound goes directly to community benefit with minimal overhead.</p>
            </div>
          </div>
        </section>

        {/* 3. Partnership Pathways Section */}
        <section id="pathways" className="pathways-section">
          <h2 className="section-title">Ways to Partner</h2>
          
          <div className="pathways-tabs">
            <button 
              className={`tab-btn ${activeTab === 'employers' ? 'active' : ''}`}
              onClick={() => setActiveTab('employers')}
            >
              For Employers
            </button>
            <button 
              className={`tab-btn ${activeTab === 'organizations' ? 'active' : ''}`}
              onClick={() => setActiveTab('organizations')}
            >
              For Organizations
            </button>
            <button 
              className={`tab-btn ${activeTab === 'funders' ? 'active' : ''}`}
              onClick={() => setActiveTab('funders')}
            >
              For Funders
            </button>
            <button 
              className={`tab-btn ${activeTab === 'professionals' ? 'active' : ''}`}
              onClick={() => setActiveTab('professionals')}
            >
              For Professionals
            </button>
          </div>

          <div className="pathway-content-container">
            {/* Employers Tab */}
            {activeTab === 'employers' && (
              <div className="pathway-content employers-content">
                <div className="employers-header">
                  <div className="employers-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </div>
                  <h3>{partnerData.forEmployers.title}</h3>
                  <p className="pathway-description">{partnerData.forEmployers.description}</p>
                </div>
                
                <div className="employers-opportunities">
                  {partnerData.forEmployers.opportunities.map((opp, idx) => (
                    <div key={idx} className="opportunity-card">
                      <div className="opportunity-number">{String(idx + 1).padStart(2, '0')}</div>
                      <div className="opportunity-content">
                        <h4>{opp.type}</h4>
                        <p>{opp.description}</p>
                      </div>
                      <div className="opportunity-arrow">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="pathway-cta">
                  <Link to="/contact" className="btn-primary">Connect with our Employment Team</Link>
                </div>
              </div>
            )}

            {/* Organizations Tab */}
            {activeTab === 'organizations' && (
              <div className="pathway-content organizations-content">
                <div className="tab-header">
                  <div className="tab-icon organizations-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                      <path d="M2 17l10 5 10-5"></path>
                      <path d="M2 12l10 5 10-5"></path>
                    </svg>
                  </div>
                  <h3>{partnerData.forOrganizations.title}</h3>
                  <p className="pathway-description">{partnerData.forOrganizations.description}</p>
                </div>
                
                <div className="opportunities-list-horizontal">
                  {partnerData.forOrganizations.partnershipModels.map((model, idx) => (
                    <div key={idx} className="opportunity-card-horizontal organizations-card">
                      <div className="opportunity-number">{String(idx + 1).padStart(2, '0')}</div>
                      <div className="opportunity-content">
                        <h4>{model.model}</h4>
                        <p>{model.description}</p>
                      </div>
                      <div className="opportunity-arrow">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="pathway-cta">
                  <Link to="/contact" className="btn-primary">Discuss Collaboration</Link>
                </div>
              </div>
            )}

            {/* Funders Tab */}
            {activeTab === 'funders' && (
              <div className="pathway-content funders-content">
                <div className="tab-header">
                  <div className="tab-icon funders-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                    </svg>
                  </div>
                  <h3>{partnerData.forFunders.title}</h3>
                  <p className="pathway-description">{partnerData.forFunders.description}</p>
                </div>
                
                <div className="funding-priorities">
                  {partnerData.forFunders.fundingNeeds.map((need, idx) => (
                    <div key={idx} className="funding-card">
                      <div className="funding-number">{String(idx + 1).padStart(2, '0')}</div>
                      <div className="funding-content">
                        <h4>{need.priority}</h4>
                        <p className="funding-description">{need.description}</p>
                        <div className="funding-impact">
                          <span className="impact-label">Impact:</span>
                          <span className="impact-text">{need.impact}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="pathway-cta">
                  <Link to="/contact" className="btn-primary">Partner as a Funder</Link>
                </div>
              </div>
            )}

            {/* Professionals Tab */}
            {activeTab === 'professionals' && (
              <div className="pathway-content professionals-content">
                <div className="tab-header">
                  <div className="tab-icon professionals-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                  <h3>Professionals: Skills-Based Volunteering</h3>
                  <p className="pathway-description">
                    Offer your professional skills on a pro-bono basis to provide expertise APT couldn't otherwise afford.
                  </p>
                </div>
                
                <div className="opportunities-list-horizontal">
                  <div className="opportunity-card-horizontal professionals-card">
                    <div className="opportunity-number">01</div>
                    <div className="opportunity-content">
                      <h4>Who We Need</h4>
                      <p>Lawyers, accountants, HR professionals, marketers, IT specialists, and business consultants.</p>
                    </div>
                    <div className="opportunity-arrow">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </div>
                  <div className="opportunity-card-horizontal professionals-card">
                    <div className="opportunity-number">02</div>
                    <div className="opportunity-content">
                      <h4>Commitment</h4>
                      <p>Flexible, project-based volunteering that fits around your schedule.</p>
                    </div>
                    <div className="opportunity-arrow">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </div>
                </div>
                
                <div className="pathway-cta">
                  <Link to="/contact" className="btn-primary">Offer Pro Bono Support</Link>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* 4. Voices of Partnership / Logos */}
        <section className="voices-section">
          <h2 className="section-title">Our Network of Impact</h2>
          <p className="section-subtitle">Proudly collaborating with these organizations</p>
          
          <div className="partner-logos-container">
            <div className="partner-logos-grid">
              <img src={mayorLogo} alt="Supported by Mayor of London" className="partner-logo" />
              <img src={sseLogo} alt="SSE" className="partner-logo" />
              <img src={shpresaLogo} alt="Shpresa Programme" className="partner-logo" />
              <img src={highfieldsLogo} alt="Highfields" className="partner-logo" />
              <img src={glaLogo} alt="Greater London Authority" className="partner-logo" />
              <img src={futureYouthLogo} alt="Future Youth Zone" className="partner-logo" />
              <img src={disabilityLogo} alt="Disability Confident" className="partner-logo" />
              <img src={barkingLogo} alt="Barking and Dagenham Council" className="partner-logo" />
            </div>
          </div>

          <div className="testimonial-card">
            <p className="testimonial-text">
              "Working with APT has allowed us to reach communities that were previously unaware of our services. 
              Their relational approach and deep community trust make them an invaluable partner in East London."
            </p>
            <p className="testimonial-author">— Community Integration Hub Partner</p>
          </div>
        </section>

        {/* 5. Final CTA */}
        <section className="final-cta-section">
          <h2>Ready to Make a Difference?</h2>
          <p>
            Whether you want to hire talent, fund a program, or integrate services, 
            we'd love to start a conversation.
          </p>
          <Link to="/contact" className="btn-secondary btn-large">Start the Conversation</Link>
        </section>

      </div>
      
      <Footer />
    </div>
  );
};

export default PartnerWithUs;
